import {
  ExceptionType,
  ListExceptionGroupsDocument,
  SpaceCheckException,
} from "~/operations";
import { Exceptions } from "~/components/exceptions/exceptions";
import {
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { useControlContext } from "~/pages/compliance/Control/ComplianceControl";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { setDocumentTitle } from "~/lib/commonUtils";
import { formatPolicyName } from "~/pages/compliance/Control/ControlOutlets/controlOutletUtils";

export type ComplianceControlExceptionsProps = {};

export function ComplianceControlExceptions({}: ComplianceControlExceptionsProps) {
  const { space, controlMrn, control, frameworkName } = useControlContext();
  const { frameworkId, controlId } = useParams();

  const { exceptionGroups, loadingListExceptionGroup } = useFetchExceptions({
    mrn: controlMrn,
    scopeMrn: space.mrn,
    types: [ExceptionType.Security],
  });

  const buildUrl = (check: SpaceCheckException) => {
    return `/space/compliance/${frameworkId}/controls/${controlId}/check?spaceId=${space.id}&findingMrn=${check.mrn}`;
  };

  setDocumentTitle([
    "Exceptions",
    control.title,
    frameworkName,
    "Compliance",
    space.name,
  ]);

  return (
    <Exceptions
      isLoading={loadingListExceptionGroup}
      findingMrn={controlMrn}
      exceptionTypes={[ExceptionType.Security]}
      emptyStateType="control-exceptions"
      exceptionGroups={exceptionGroups}
      refetchQueries={[ListExceptionGroupsDocument]}
      renderTable={function (exceptionGroup: ExceptionGroup) {
        const { exceptions } = exceptionGroup;
        return (
          <Table
            sx={{
              borderRadius: 1,
              overflow: "hidden",
              "& a:last-child td": {
                borderBottom: "none",
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "background.lighter",
                  "&:hover": { backgroundColor: "background.lighter" },
                }}
              >
                <TableCell>Result</TableCell>
                <TableCell>Check</TableCell>
                <TableCell>Assets</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(exceptions as SpaceCheckException[]).map((check) => {
                return (
                  <TableRow
                    sx={{
                      cursor: "pointer",
                      backgroundColor: "background.light",
                    }}
                    component={RouterLink}
                    to={buildUrl(check)}
                  >
                    <TableCell>
                      {!!check.riskValue && (
                        <ImpactUpdated
                          isRiskScore
                          impact={{
                            value: check.riskValue,
                            rating: check.rating,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        className="mquery-title"
                        fontSize={14}
                        display="inline-block"
                      >
                        {check.title}
                      </Typography>
                      <List sx={{ p: 0 }}>
                        <ListItem
                          sx={{ p: 0 }}
                          key={formatPolicyName(check.policyMrn, space.id)}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontSize={10}
                          >
                            {formatPolicyName(check.policyMrn, space.id)}
                          </Typography>
                        </ListItem>
                      </List>
                    </TableCell>
                    <TableCell>{check.assetsStats.total}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        );
      }}
    />
  );
}
